/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import validate from 'validate.js';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { Button, TextField, Typography, Grid, Select } from '@mui/material';
import useRouter from 'utils/useRouter';
import { SetFormState, GetOperatorProjects } from '../../../../../../actions/UsersActions';
import SelectField from '../../../../../../../../components/SelectField/SelectField.js';

const schema = {
	firstname: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	lastname: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	email: {
		presence: { allowEmpty: false, message: 'is required' },
		email: true,
	},
	roleId: {
		presence: { allowEmpty: false, message: 'is required' },
	},
};

const useStyles = makeStyles((theme) => ({
	root: {},
	fields: {
		margin: theme.spacing(-1),
		display: 'flex',
		flexWrap: 'wrap',
		'& > *': {
			flexGrow: 1,
			margin: theme.spacing(1),
		},
	},
	submitButton: {
		marginTop: theme.spacing(2),
		width: '100%',
	},
}));

const AddEditUserForm = (props) => {
	const { className, roles, operators, projects, isEdit, ...rest } = props;

	const classes = useStyles();
	const router = useRouter();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.usersReducer.formState);

	const usePrevious = (value) => {
		const ref = useRef();
		useEffect(() => {
			ref.current = value;
		});
		return ref.current;
	};

	const useHasChanged = (val) => {
		const prevVal = usePrevious(val);
		return prevVal !== val;
	};

	const hasOperatorIdsChanged = useHasChanged(formState.values.operatorIds);

	useEffect(() => {
		const errors = validate(formState.values, schema);

		setFormState({
			...formState,
			isValid: !errors,
			errors: errors || {},
		});

		if (hasOperatorIdsChanged) {
			dispatch(GetOperatorProjects(formState.values.operatorIds || []));
		}
	}, [formState.values]);

	const handleChange = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});
	};

	const hasError = (field) => (!!(formState.touched[field] && formState.errors[field]));
	return (
		<div className={classes.fields}>
			<Grid container spacing={4}>
				<Grid item md={6} xs={12}>
					<TextField
						error={hasError('firstname')}
						fullWidth
						helperText={
							hasError('firstname') ? formState.errors.firstname[0] : null
						}
						label="First name"
						name="firstname"
						onChange={handleChange}
						value={formState.values.firstname || ''}
						variant="outlined"
					/>
				</Grid>
				<Grid item md={6} xs={12}>
					<TextField
						error={hasError('lastname')}
						fullWidth
						helperText={
							hasError('lastname') ? formState.errors.lastname[0] : null
						}
						label="Last name"
						name="lastname"
						onChange={handleChange}
						value={formState.values.lastname || ''}
						variant="outlined"
					/>
				</Grid>
				<Grid item md={12} xs={12}>
					<TextField
						error={hasError('email')}
						fullWidth
						helperText={
							hasError('email') ? formState.errors.email[0] : null
						}
						label="Email address"
						name="email"
						onChange={handleChange}
						value={formState.values.email || ''}
						disabled={isEdit}
						variant="outlined"
					/>
				</Grid>
				<Grid item md={4} xs={12}>
					<SelectField
						id="roles"
						name="roleId"
						value={formState.values.roleId || ''}
						onChange={handleChange}
						hintText="Role"
						menuItems={roles}
						error={hasError('roleId') ? formState.errors.roleId[0] : null}
					/>
				</Grid>
				<Grid item md={4} xs={12}>
					<SelectField
						id="operators"
						name="operatorIds"
						value={formState.values.operatorIds || []}
						onChange={handleChange}
						hintText="Operator"
						menuItems={operators}
						isMultiSelect
						error={hasError('operatorIds') ? formState.errors.operatorIds[0] : null}
					/>
				</Grid>
				<Grid item md={4} xs={12}>
					<SelectField
						id="projects"
						name="projectIds"
						value={formState.values.projectIds || []}
						onChange={handleChange}
						hintText="Projects"
						menuItems={projects}
						isMultiSelect
						error={hasError('projectIds') ? formState.errors.projectIds[0] : null}
					/>
				</Grid>
			</Grid>
		</div>

	);
};

AddEditUserForm.propTypes = {
	className: PropTypes.string,
	roles: PropTypes.array,
	operators: PropTypes.array,
	projects: PropTypes.array,
	isEdit: PropTypes.bool,
};

export default AddEditUserForm;
